/**
 * nutri-leaf-header.js
 * Nutri-Leaf header — mobile nav, category dropdown, mega panels,
 * --header-height CSS var, cart count live update.
 * Custom Element (Dawn convention): lifecycle via connectedCallback /
 * disconnectedCallback. All global listeners (resize, document click /
 * keydown, cart:refresh) are removed on disconnect.
 */
(function () {
  'use strict';

  class NLHeaderSection extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      const sectionId = this.dataset.sectionId;

      // --header-height CSS var; listener removed on disconnect
      this.setHeaderHeight = () => {
        const el = document.getElementById('nl-header-' + sectionId);
        if (!el) return;
        // MB-29 (2026-08-31): offsetHeight alone breaks whenever content
        // sits above the header (promo bar): the drawer/sheet anchored at
        // 61px while the header spans 42..103 — the drawer covered the
        // hamburger and the only close control was gone. The anchor must be
        // the header's CURRENT viewport bottom: getBoundingClientRect()
        // tracks the sticky travel (103 below the promo bar, 61 once stuck
        // at the top), and the scroll listener (passive) keeps it fresh.
        document.documentElement.style.setProperty('--header-height', Math.round(el.getBoundingClientRect().bottom) + 'px');
      };
      window.addEventListener('resize', this.setHeaderHeight);
      window.addEventListener('scroll', this.setHeaderHeight, { passive: true });
      this.setHeaderHeight();

      // Cart count live update — one cart-change event contract (P0-10):
      // cart:refresh is dispatched by NLCart.add() and by cart.js after
      // drawer/cart-page quantity edits. URL derived from routes so it stays
      // correct under locale roots; listener removed on disconnect.
      const countEl = document.getElementById('nl-header-cart-count-' + sectionId);
      this.refreshCartCount = () => {
        if (!countEl) return;
        fetch((window.routes ? window.routes.cart_url : '/cart') + '.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            countEl.textContent = cart.item_count;
            countEl.style.display = cart.item_count > 0 ? '' : 'none';
          })
          .catch(function () {});
      };
      document.addEventListener('cart:refresh', this.refreshCartCount);
      this.refreshCartCount();

      this.hamburger = document.getElementById('nl-hamburger-' + sectionId);
      this.navRow = this.querySelector('.nl-nav-row');
      this.backdrop = this.querySelector('[data-nl-nav-backdrop]');
      this.catBtn = this.querySelector('.nl-categories-btn');
      this.catDrop = document.getElementById('nl-categories-dropdown-' + sectionId);
      this.activePanel = null;
      this.openTrigger = null;

      // Bound handler references so they can be removed on disconnect
      this.handleClick = this.onClick.bind(this);
      this.handleKeydown = this.onKeydown.bind(this);
      this.handleFocusOut = this.onFocusOut.bind(this);
      this.handleHamburgerClick = this.toggleMobileNav.bind(this);
      this.handleCategoriesClick = this.toggleCategories.bind(this);
      this.handleNavTriggerClick = this.onNavTriggerClick.bind(this);
      this.handleMobileNavKeydown = this.onMobileNavKeydown.bind(this);
      this.handleNavRowClick = this.onNavRowClick.bind(this);
      this.handleBackdropClick = this.onBackdropClick.bind(this);
      this.handleResizeNav = this.onResizeNav.bind(this);

      if (this.hamburger && this.navRow) {
        this.hamburger.addEventListener('click', this.handleHamburgerClick);
      }

      if (this.navRow) {
        this.navRow.addEventListener('click', this.handleNavRowClick);
      }

      if (this.backdrop) {
        this.backdrop.addEventListener('click', this.handleBackdropClick);
      }

      // Close the drawer if the viewport grows past the mobile breakpoint
      // while it is open (RV-26).
      window.addEventListener('resize', this.handleResizeNav);

      // FZ-13 follow-up: keep closed mega panels inside the viewport.
      // Panels are in the DOM from the start; widths are final once fonts
      // load, so clamp now, on resize, and after fonts-ready.
      this.handleClampPanels = this.clampMegaPanels.bind(this);
      window.addEventListener('resize', this.handleClampPanels);
      // PRD-20 (2026-08-30): on cold loads the first clamp can run against
      // mid-reflow geometry (the FZ-14 sheet race) and leave a stale inline
      // offset; re-clamp once everything is settled.
      window.addEventListener('load', this.handleClampPanels);
      if (this.navRow) this.clampMegaPanels();
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(this.clampMegaPanels.bind(this)).catch(() => {});
      }

      if (this.catBtn && this.catDrop) {
        this.catBtn.addEventListener('click', this.handleCategoriesClick);
      }

      // Nav triggers — click on both desktop and mobile
      this.navTriggers = this.querySelectorAll('[data-nl-menu-trigger]');
      this.navTriggers.forEach(function (btn) {
        btn.addEventListener('click', this.handleNavTriggerClick);
      }, this);

      // RV-41: mega-menu product-rail images ship as data-src so closed
      // panels (visibility:hidden) never fetch them; swap the src the first
      // time the panel is revealed — hover (mouseenter) or keyboard/focus.
      this.handleRailReveal = this.onRailReveal.bind(this);
      if (this.navRow) {
        this.navRow.addEventListener('mouseenter', this.handleRailReveal, true);
        this.navRow.addEventListener('focusin', this.handleRailReveal, true);
      }

      // Cart drawer open — progressive enhancement: open drawer if present,
      // otherwise fall back to the link's href (/cart page)
      this.cartBtn = this.querySelector('.js-open-cart-drawer');
      if (this.cartBtn) {
        this.handleCartClick = function (e) {
          var drawer = document.querySelector('cart-drawer');
          if (drawer) {
            e.preventDefault();
            drawer.open(this.cartBtn);
          }
        }.bind(this);
        this.cartBtn.addEventListener('click', this.handleCartClick);
      }

      // Document-level listeners
      document.addEventListener('click', this.handleClick);
      document.addEventListener('keydown', this.handleKeydown);
      this.addEventListener('focusout', this.handleFocusOut);
    }

    disconnectedCallback() {
      if (!this.initialized) return;
      this.initialized = false;
      window.removeEventListener('resize', this.setHeaderHeight);
      document.removeEventListener('cart:refresh', this.refreshCartCount);
      document.removeEventListener('click', this.handleClick);
      document.removeEventListener('keydown', this.handleKeydown);
      this.removeEventListener('focusout', this.handleFocusOut);
      if (this.hamburger) this.hamburger.removeEventListener('click', this.handleHamburgerClick);
      if (this.navRow) this.navRow.removeEventListener('click', this.handleNavRowClick);
      if (this.backdrop) this.backdrop.removeEventListener('click', this.handleBackdropClick);
      window.removeEventListener('resize', this.handleResizeNav);
      window.removeEventListener('resize', this.handleClampPanels);
      window.removeEventListener('load', this.handleClampPanels);
      if (this.catBtn) this.catBtn.removeEventListener('click', this.handleCategoriesClick);
      if (this.cartBtn) this.cartBtn.removeEventListener('click', this.handleCartClick);
      if (this.navTriggers) {
        this.navTriggers.forEach(function (btn) {
          btn.removeEventListener('click', this.handleNavTriggerClick);
        }, this);
      }
      if (this.navRow) {
        this.navRow.removeEventListener('mouseenter', this.handleRailReveal, true);
        this.navRow.removeEventListener('focusin', this.handleRailReveal, true);
      }
      if (this.navRow) this.navRow.removeEventListener('click', this.handleNavRowClick);
      if (this.backdrop) this.backdrop.removeEventListener('click', this.handleBackdropClick);
      window.removeEventListener('resize', this.handleResizeNav);
      window.removeEventListener('scroll', this.setHeaderHeight);
    }

    // Single close path (RV-26): every route out of the mobile nav — hamburger
    // toggle, Escape, outside click, breakpoint — must undo ALL open side
    // effects: classes, ARIA, scroll lock, keydown trap, panels, focus.
    closeMobileNav(returnFocus) {
      if (!this.navRow || !this.navRow.classList.contains('nl-nav-row--open')) return;
      this.navRow.classList.remove('nl-nav-row--open');
      if (this.hamburger) {
        this.hamburger.setAttribute('aria-expanded', 'false');
        this.hamburger.classList.remove('nl-hamburger-btn--open');
      }
      document.documentElement.style.overflow = '';
      document.documentElement.classList.remove('nl-nav-open');
      this.navRow.removeEventListener('keydown', this.handleMobileNavKeydown);
      if (this.backdrop) this.backdrop.hidden = true;
      this.closeAllPanels();
      if (returnFocus !== false && this.hamburger) this.hamburger.focus();
    }

    toggleMobileNav() {
      if (this.navRow.classList.contains('nl-nav-row--open')) {
        this.closeMobileNav();
        return;
      }
      this.navRow.classList.add('nl-nav-row--open');
      this.hamburger.setAttribute('aria-expanded', 'true');
      this.hamburger.classList.add('nl-hamburger-btn--open');
      document.documentElement.style.overflow = 'hidden';
      document.documentElement.classList.add('nl-nav-open');
      this.navRow.addEventListener('keydown', this.handleMobileNavKeydown);
      if (this.backdrop) this.backdrop.hidden = false;
      // Move focus to first focusable element in nav
      var firstFocusable = this.navRow.querySelector('a, button, [tabindex]:not([tabindex="-1"])');
      if (firstFocusable) firstFocusable.focus();
    }

    toggleCategories() {
      const open = this.catDrop.classList.toggle('nl-categories-dropdown--open');
      this.catBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    onNavTriggerClick(e) {
      this.togglePanel(e.currentTarget);
    }

    onMobileNavKeydown(e) {
      if (e.key !== 'Tab') return;
      var focusable = this.navRow.querySelectorAll('a, button, [tabindex]:not([tabindex="-1"])');
      if (focusable.length === 0) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    // Close drawer when a real link inside it is activated; navigation
    // proceeds normally (no preventDefault). Accordion <button>s don't match.
    onNavRowClick(e) {
      var link = e.target.closest('a');
      if (link && this.navRow.classList.contains('nl-nav-row--open')) {
        this.closeMobileNav(false);
      }
    }

    onBackdropClick() {
      this.closeMobileNav();
    }

    // Breakpoint close (RV-26): growing past the mobile drawer breakpoint
    // must release scroll lock and drawer state.
    onResizeNav() {
      if (this.navRow && this.navRow.classList.contains('nl-nav-row--open') && window.innerWidth > 989) {
        this.closeMobileNav();
      }
    }

    togglePanel(trigger) {
      const panel = trigger.nextElementSibling;
      if (!panel || !panel.classList.contains('nl-nav-mega')) return;

      if (this.activePanel === panel) {
        // Close current
        this.closePanel(panel, trigger);
      } else {
        // Close previous, open new
        if (this.activePanel) this.closePanel(this.activePanel, this.openTrigger);
        this.openPanel(panel, trigger);
      }
    }

    openPanel(panel, trigger) {
      panel.classList.add('nl-nav-mega--open');
      panel.setAttribute('aria-hidden', 'false');
      trigger.setAttribute('aria-expanded', 'true');
      this.activePanel = panel;
      this.openTrigger = trigger;
      // PR-15 (2026-08-17): raise the whole header stacking context while a
      // mega panel is open — third-party widgets (Trustpilot embed, z-index
      // 999999999) otherwise paint above the panel because nl-header-section
      // sits at --nl-z-nav (100).
      this.classList.add('nl-mega-open');
    }

    onRailReveal(event) {
      const item = event.target.closest('.nl-nav-item--has-dropdown');
      if (!item) return;
      const imgs = item.querySelectorAll('.nl-nav-mega__product-img[data-src]');
      imgs.forEach(function (img) {
        if (!img.getAttribute('src')) img.src = img.dataset.src;
      });
      // Rail images load lazily — re-clamp in case they shift panel width.
      this.clampMegaPanels();
    }

    // FZ-13 follow-up (2026-08-14): mega panels are sized to content and
    // anchored to their trigger, so a wide panel (merchant-added columns,
    // RV-41 product rail) can extend past the viewport edge and scroll the
    // page — closed (visibility:hidden) panels still count toward scrollable
    // overflow. CSS cannot clamp an absolutely-positioned panel to the
    // viewport, so shift it 16px inside the viewport edge. Runs on
    // init/resize/fonts-ready and on first reveal (hover and keyboard);
    // hover-only panels were already clamped at init.
    clampMegaPanels() {
      if (window.innerWidth <= 989) return; // mobile accordion: static panels
      const m = 16;
      this.querySelectorAll('.nl-nav-mega').forEach((panel) => {
        // CR-01: clear previous clamps first — a stale inline offset from a
        // narrower viewport otherwise persists after the panel stops
        // overflowing (the one-way latch never clears itself).
        panel.style.left = '';
        panel.style.right = '';
        const rect = panel.getBoundingClientRect();
        const parentRect = panel.offsetParent ? panel.offsetParent.getBoundingClientRect() : null;
        // PRD-20 (2026-08-30): Chrome's getComputedStyle never returns
        // 'auto' for left/right on a positioned box — it resolves the USED
        // offset — so the old `cs.left !== 'auto'` anchor check was always
        // true, and a right-anchored pillar panel whose trigger sits at the
        // viewport's right edge was clamped by the wrong edge (DE 1024:
        // contact panel spilled to scrollWidth 1905). Detect the anchor
        // geometrically instead.
        const anchoredRight = !!parentRect && Math.abs(rect.right - parentRect.right) < 2;
        const rightOver = rect.right - (window.innerWidth - m);
        const leftOver = m - rect.left;
        if (rightOver > 0 && anchoredRight) {
          // Right-anchored pillar panel: shift its right edge 16px inside
          // the viewport (right: N keeps the CSS right:0 anchor, unlike a
          // left: Npx re-anchor which is relative to the item, not the
          // viewport). PRD-20 (2026-08-30): DE contact panel spilled to
          // scrollWidth 1905 at 1024px without this.
          panel.style.right = rightOver + 'px';
        } else if (rightOver > 0) {
          panel.style.left = '-' + rightOver + 'px';
        } else if (leftOver > 0) {
          panel.style.right = '-' + leftOver + 'px';
        }
      });
    }

    closePanel(panel, trigger) {
      panel.classList.remove('nl-nav-mega--open');
      panel.setAttribute('aria-hidden', 'true');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (this.activePanel === panel) {
        this.activePanel = null;
        this.openTrigger = null;
      }
      this.classList.remove('nl-mega-open');
    }

    closeAllPanels() {
      this.querySelectorAll('.nl-nav-mega--open').forEach((panel) => {
        panel.classList.remove('nl-nav-mega--open');
        panel.setAttribute('aria-hidden', 'true');
        const trigger = panel.previousElementSibling;
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      });
      this.activePanel = null;
      this.openTrigger = null;
      this.classList.remove('nl-mega-open');
    }

    onClick(e) {
      if (!e.target.closest('.nl-header__search')) {
        if (this.catDrop) this.catDrop.classList.remove('nl-categories-dropdown--open');
        if (this.catBtn) this.catBtn.setAttribute('aria-expanded', 'false');
      }
      // Close panels on outside click (desktop and mobile)
      if (this.activePanel && !e.target.closest('.nl-nav-item--has-dropdown')) {
        this.closeAllPanels();
      }
    }

    onKeydown(e) {
      if (e.key === 'Escape') {
        // Close category dropdown first if open
        if (this.catDrop && this.catDrop.classList.contains('nl-categories-dropdown--open')) {
          this.catDrop.classList.remove('nl-categories-dropdown--open');
          if (this.catBtn) {
            this.catBtn.setAttribute('aria-expanded', 'false');
            this.catBtn.focus();
          }
          e.preventDefault();
          return;
        }
        if (this.activePanel) {
          const trigger = this.openTrigger;
          this.closeAllPanels();
          if (trigger) trigger.focus();
          e.preventDefault();
        } else if (this.navRow && this.navRow.classList.contains('nl-nav-row--open')) {
          this.closeMobileNav();
          e.preventDefault();
        }
      }
    }

    onFocusOut(e) {
      // Close open panel when focus leaves the menu item entirely.
      // Use relatedTarget (element gaining focus), not target (element losing it).
      var entering = e.relatedTarget;
      if (this.activePanel && !(entering && entering.closest('.nl-nav-item--has-dropdown'))) {
        // Small delay to let focus land
        var self = this;
        setTimeout(function () {
          if (self.activePanel && !self.contains(document.activeElement)) {
            self.closeAllPanels();
          }
        }, 100);
      }
    }
  }

  if (!customElements.get('nl-header-section')) {
    customElements.define('nl-header-section', NLHeaderSection);
  }

  // Combined localization picker: one button opens Languages + Countries
  // lists; selecting an item submits the matching hidden localization form.
  class NLLocalizationPicker extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.button = this.querySelector('[data-nl-loc-toggle]');
      this.panel = this.querySelector('[data-nl-loc-panel]');
      if (!this.button || !this.panel) return;

      this.onToggle = this.toggle.bind(this);
      this.onItemClick = this.onItemClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);

      this.button.addEventListener('click', this.onToggle);
      this.addEventListener('keydown', this.onKeydown);
      document.addEventListener('click', this.onDocumentClick);
      this.querySelectorAll('[data-nl-loc-item]').forEach((item) => {
        item.addEventListener('click', this.onItemClick);
      });
    }

    disconnectedCallback() {
      if (!this.initialized) return;
      this.button.removeEventListener('click', this.onToggle);
      this.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('click', this.onDocumentClick);
      this.querySelectorAll('[data-nl-loc-item]').forEach((item) => {
        item.removeEventListener('click', this.onItemClick);
      });
    }

    toggle() {
      const open = this.button.getAttribute('aria-expanded') !== 'true';
      this.button.setAttribute('aria-expanded', String(open));
      this.panel.hidden = !open;
      if (open) this.button.focus();
    }

    onItemClick(event) {
      event.preventDefault();
      const item = event.currentTarget;
      const kind = item.dataset.nlLocKind; // 'language' | 'country'
      const input = this.querySelector('input[data-nl-loc-form="' + kind + '"]');
      if (!input) return;
      input.value = item.dataset.nlLocCode;
      const form = input.closest('form');
      if (form) form.submit();
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.button.getAttribute('aria-expanded') === 'true') {
        event.stopPropagation();
        this.toggle();
        this.button.focus();
      }
    }

    onDocumentClick(event) {
      if (this.button.getAttribute('aria-expanded') !== 'true') return;
      if (!event.target.closest('nl-localization-picker')) {
        this.toggle();
      }
    }
  }

  if (!customElements.get('nl-localization-picker')) {
    customElements.define('nl-localization-picker', NLLocalizationPicker);
  }
})();
